// external
import { Observable, throwError } from "rxjs";
import { saveAs } from "file-saver";

// angular
import {
  HttpErrorResponse,
  HttpHeaders,
  HttpResponse,
} from "@angular/common/http";

// local
import { ZodError, type ZodType } from "zod";

import {
  ApiResponse,
  IProblemDetails,
  PROBLEM_STANDARD_KEYS,
  ProblemDetailsWireSchema,
} from "../models";
import { validateWire } from "./wire-validation.class";

// Set view of the shared key list (declared next to IProblemDetails, the
// single source of truth) for the extension-member filter below.
const PROBLEM_STANDARD_KEY_SET: ReadonlySet<string> = new Set(
  PROBLEM_STANDARD_KEYS,
);

export class HttpUtil {
  public static parseError(res: any): ApiResponse {
    const status = res.status;

    if (!status) {
      return ApiResponse.Error(0, "Offline");
    }

    // Every /api error is an RFC 9457 ProblemDetails body: detail carries
    // the human-readable message (title is the generic status phrase),
    // errors the per-field validation map, and extension members any
    // structured payload (carried through on `data` so e.g. the snippet
    // unpublish blockedBy list reaches the caller). Validate leniently: a
    // body that does not conform (a gateway 502, a non-JSON error) is parsed
    // best-effort rather than discarded, so the caller still gets a status.
    if (res instanceof HttpErrorResponse) {
      const parsed = ProblemDetailsWireSchema.safeParse(res.error);
      const problem: IProblemDetails | null = parsed.success
        ? parsed.data
        : (res.error as IProblemDetails | null);
      if (problem && (problem.detail || problem.title)) {
        return ApiResponse.Error(
          status,
          problem.detail ?? problem.title ?? "Request failed",
          problem.errors,
          HttpUtil.problemExtensions(problem),
        );
      }
    }

    // best effort
    const defaultMessage = res.statusText || res.status || res.toString();
    return ApiResponse.Error(status, defaultMessage);
  }

  /** Collect a problem body's extension members; null when there are none. */
  private static problemExtensions(
    problem: IProblemDetails,
  ): Record<string, unknown> | null {
    let extensions: Record<string, unknown> | null = null;

    for (const [key, value] of Object.entries(problem)) {
      if (PROBLEM_STANDARD_KEY_SET.has(key)) {
        continue;
      }
      extensions = extensions ?? {};
      extensions[key] = value;
    }

    return extensions;
  }

  public static handleError(error: any): Observable<ApiResponse> {
    // A ZodError is the dev-mode wire-validation rethrow (validateWire): let
    // it propagate unchanged so contract drift surfaces loudly on the
    // observable's error channel instead of collapsing into an ApiResponse.
    return throwError(() =>
      error instanceof ZodError ? error : HttpUtil.parseError(error),
    );
  }

  public static getFileName(headers: HttpHeaders): string | null {
    const contentDisposition = headers.get("content-disposition");

    if (!contentDisposition) {
      return null;
    }

    const match = contentDisposition.match(
      /filename[^;\n=]*=((['"]).*?\2|[^;\n]*)/,
    );

    if (!match || match.length < 2) {
      return null;
    }

    return match[1];
  }

  // The single validation seam: every payload entering the app is validated
  // here, against the wire schema the request declared in its
  // IApiRequestArgs (an entity service passes its serializer's schema). See
  // design/zod-frontend-validation/PLAN.md. The response body IS the
  // payload (no envelope); a 204 / empty body parses to null.
  private static parseData(body: unknown, schema?: ZodType): any {
    // Only a genuinely absent payload is null; a falsy-but-valid value (0,
    // false, "") must reach validation, not collapse to null. `== null`
    // catches both null and undefined.
    if (body == null) {
      return null;
    }

    if (!schema) {
      return body;
    }

    if (body instanceof Array) {
      return body.map((item) => validateWire(schema, item));
    }

    return validateWire(schema, body);
  }

  public static parseBlobResponse(res: HttpResponse<Blob>): ApiResponse {
    const status = res.status;

    if (status < 200 || status >= 400) {
      throw res;
    }

    try {
      // check if response was a file
      const fileName = HttpUtil.getFileName(res.headers);
      if (!fileName) {
        return ApiResponse.Error(status, "faield to parse file name");
      }

      const blob: Blob | null = res.body;
      if (!blob) {
        return ApiResponse.Error(status, "null blob returned");
      }

      console.log("File download ok", fileName);
      saveAs(blob, fileName);
      return ApiResponse.File(status, fileName, blob);
    } catch (err) {
      console.log("http error", err);
      return ApiResponse.Error(0, String(err));
    }
  }

  public static parseResponse(
    res: HttpResponse<unknown>,
    schema?: ZodType,
  ): ApiResponse {
    const status = res.status;

    if (status < 200 || status >= 400) {
      throw res;
    }

    try {
      const data = HttpUtil.parseData(res.body, schema);
      return ApiResponse.Success(res.status, data);
    } catch (err) {
      // Don't swallow the dev-mode wire-validation rethrow: a ZodError must
      // reach the caller (via handleError) so drift is loud in development.
      if (err instanceof ZodError) {
        throw err;
      }
      console.log("http error", err, res.body);
      return ApiResponse.Error(0, String(err));
    }
  }
}
